import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import Navigation from "../../Navigation";
import Footer from "../../Footer";

const SketchingLeastSquaresPage = () => {
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);

  // 初始化section refs
  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 2);
  }, []);

  // 处理滚动
  useEffect(() => {
    const handleScroll = () => {
      const currentPosition = window.pageYOffset + 300;
      let active = 0;

      sectionRefs.current.forEach((ref, index) => {
        if (ref && ref.offsetTop <= currentPosition) {
          active = index;
        }
      });

      if (active !== activeSection) {
        setActiveSection(active);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection]);

  // 翻译
  const translations = {
    backToProjects: {
      en: "Back to Projects",
      zh: "返回项目列表",
      ja: "プロジェクトに戻る",
    },
    title: {
      en: "Sketching for Least Squares Problem",
      zh: "最小二乘问题的降维技术",
      ja: "最小二乗問題のためのスケッチング",
    },
    tagline: {
      en: "Efficient dimensionality reduction techniques for solving large-scale least squares problems",
      zh: "用于解决大规模最小二乘问题的高效降维技术",
      ja: "大規模な最小二乗問題を解くための効率的な次元削減技術",
    },
    overview: {
      en: "Project Overview",
      zh: "项目概述",
      ja: "プロジェクト概要",
    },
    description: {
      en: "A dimensionality reduction approach for solving large-scale least squares problems with lower computational cost while maintaining high accuracy.",
      zh: "使用降维技术高效解决大规模最小二乘问题，降低计算成本同时保持高精度。",
      ja: "計算コストを削減しながら高い精度を維持する大規模な最小二乗問題のための次元削減アプローチ。",
    },
    methodology: {
      en: "Methodology",
      zh: "方法论",
      ja: "方法論",
    },
    methodologyText: {
      en: "Implemented two sketching techniques for solving large-scale least squares problems (min‖Ax - b‖₂):\n\n• Gaussian Embeddings: Random matrices with normal distribution entries\n• CountSketch: Sparse random matrices with faster computation\n\nEvaluated using NYC Motor Vehicle Collision dataset against traditional approaches (QR, Normal Equations, SVD).",
      zh: "实现了两种sketching技术解决大规模最小二乘问题(min‖Ax - b‖₂)：\n\n• 高斯嵌入：正态分布随机矩阵\n• CountSketch：计算更快的稀疏随机矩阵\n\n使用纽约市车辆碰撞数据集与传统方法(QR、正规方程、SVD)进行比较评估。",
      ja: "大規模な最小二乗問題(min‖Ax - b‖₂)を解くための2つのスケッチング技術を実装：\n\n• ガウス埋め込み：正規分布のランダム行列\n• CountSketch：より高速な計算のための疎なランダム行列\n\nニューヨーク市の車両衝突データセットを使用して従来のアプローチ(QR、正規方程式、SVD)と比較評価。",
    },
    results: {
      en: "Key Findings",
      zh: "主要发现",
      ja: "主な調査結果",
    },
    resultsText: {
      en: "• Both methods achieved high accuracy with small sketch dimensions\n• CountSketch provided better runtime performance\n• Gaussian Sketch delivered tighter residuals\n• Reduced computation by orders of magnitude for large datasets",
      zh: "• 两种方法在小尺寸草图下都实现了高准确度\n• CountSketch提供更好的运行时性能\n• 高斯Sketch提供更紧凑的残差\n• 大型数据集的计算量减少了数个数量级",
      ja: "• 両方の方法は小さいスケッチ次元で高い精度を達成\n• CountSketchはより良い実行時パフォーマンスを提供\n• ガウススケッチはより厳密な残差を提供\n• 大規模データセットの計算量を桁違いに削減",
    },
    courseInfo: {
      en: "Course Information",
      zh: "课程信息",
      ja: "コース情報",
    },
    course: {
      en: "Course:",
      zh: "课程:",
      ja: "コース:",
    },
    instructor: {
      en: "Instructor:",
      zh: "教师:",
      ja: "教授:",
    },
    pdfPreview: {
      en: "Project Report Preview",
      zh: "项目报告预览",
      ja: "プロジェクトレポートプレビュー",
    },
    viewFullReport: {
      en: "Click to view full report",
      zh: "点击查看完整报告",
      ja: "クリックして完全なレポートを表示",
    },
    closeFullView: {
      en: "Close full view",
      zh: "关闭全屏查看",
      ja: "全画面表示を閉じる",
    },
  };

  // 技术栈数据
  const techStack = [
    { name: "Python", category: "language", color: "blue" },
    { name: "NumPy", category: "library", color: "green" },
    { name: "SciPy", category: "library", color: "green" },
    { name: "Pandas", category: "library", color: "green" },
    { name: "Matplotlib", category: "library", color: "green" },
    { name: "Linear Algebra", category: "concept", color: "purple" },
    { name: "Dimensionality Reduction", category: "concept", color: "purple" },
    { name: "Statistical Analysis", category: "feature", color: "teal" },
  ];

  // 按类别获取技术标签的颜色
  const getTagColor = (category) => {
    switch (category) {
      case "language":
        return "bg-blue-100 text-blue-800";
      case "library":
        return "bg-green-100 text-green-800";
      case "concept":
        return "bg-purple-100 text-purple-800";
      case "feature":
        return "bg-teal-100 text-teal-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50">
      <Navigation activeSection="projects" />
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-6 flex justify-between items-center">
            <a
              href="/projects"
              className="text-primary-500 hover:text-primary-600 transition-colors flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {translations.backToProjects[language] ||
                translations.backToProjects.en}
            </a>
          </div>

          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-light mb-4 text-neutral-800">
              {translations.title[language] || translations.title.en}
            </h1>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              {translations.tagline[language] || translations.tagline.en}
            </p>

            {/* 技术栈标签 */}
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {techStack.map((tech, index) => (
                <span
                  key={index}
                  className={`${getTagColor(
                    tech.category
                  )} px-3 py-1 rounded-full text-sm font-medium`}
                >
                  {tech.name}
                </span>
              ))}
            </div>
          </header>

          {/* 项目概述 */}
          <section
            ref={(el) => (sectionRefs.current[0] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.overview[language] || translations.overview.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.description[language] ||
                translations.description.en}
            </p>

            {/* 项目展示图 */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
              <div className="p-2">
                <img
                  src="/assets/projects/sketchingLeastSquares/main_image.png"
                  alt="Sketching for Least Squares visualization"
                  className="w-full rounded-lg"
                />
                <div className="text-center text-sm text-neutral-500 mt-2">
                  {language === "en"
                    ? "Sketching methods for dimensionality reduction in least squares problems"
                    : language === "zh"
                    ? "最小二乘问题中的降维sketching方法"
                    : "最小二乗問題における次元削減のためのスケッチング手法"}
                </div>
              </div>
            </div>

            {/* 课程信息 */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h3 className="text-lg font-medium mb-4 text-neutral-800">
                {translations.courseInfo[language] ||
                  translations.courseInfo.en}
              </h3>
              <p className="text-neutral-700 mb-2">
                <span className="font-medium">
                  {translations.course[language] || translations.course.en}
                </span>{" "}
                MATH 252: Numerical Analysis
              </p>
              <p className="text-neutral-700 mb-2">
                <span className="font-medium">
                  {translations.instructor[language] ||
                    translations.instructor.en}
                </span>{" "}
                Prof. Tyler Chen
              </p>
            </div>
          </section>

          {/* 方法论 */}
          <section
            ref={(el) => (sectionRefs.current[1] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.methodology[language] ||
                translations.methodology.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed whitespace-pre-line">
              {translations.methodologyText[language] ||
                translations.methodologyText.en}
            </p>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="md:flex">
                <div className="md:w-1/2 p-6 flex flex-col justify-center">
                  <h3 className="text-xl font-medium mb-3 text-neutral-800">
                    {translations.results[language] || translations.results.en}
                  </h3>
                  <p className="text-neutral-600 whitespace-pre-line">
                    {translations.resultsText[language] ||
                      translations.resultsText.en}
                  </p>
                </div>
                <div className="md:w-1/2 bg-gray-50 p-4 flex items-center justify-center">
                  <img
                    src="/assets/projects/sketchingLeastSquares/illustration.png"
                    alt="Sketching methodology diagram"
                    className="max-w-full rounded-lg"
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SketchingLeastSquaresPage;
